import { useForm, Controller } from "react-hook-form";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { Link } from "@material-ui/core";
import STBoardForm from "../../assets/styles/organisms/boardForm.module.scss";
import STForm from "../../assets/styles/atoms/form/reactHookForms/form.module.scss";
import STSelectBox from "../../assets/styles/atoms/form/reactHookForms/selectBox.module.scss";
import STButton from "../../assets/styles/atoms/button.module.scss";
import Label from "../atoms/form/reactHookForms/Label";
import CloseIcon from "@material-ui/icons/Close";
import { FormField, FormValues } from "../../types/Board";
import {
  useFormConfig,
  useGetBoardCategories,
  useMutationBoardForm,
} from "../../hooks/useBoardForm";
import SnackbarMessage from "../atoms/Snackbar";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { SiteContext } from "../../providers/SiteProvider";
import MemberUpdateNicknameAndAvatar from "./MemberUpdateNicknameAndAvatar";
import { LoginUserContext } from "../../providers/LoginUserProvider";
import { fetchEditBoardDetail } from "../../utils/api/fetchEditBoardDetail";
import { FORUM_APPROVAL_SETTING } from "../../utils/boardDetailConstants";

const FieldWrapper: React.FC<{
  title: string;
  htmlFor?: string;
  isRequired?: boolean;
  messageError?: string;
}> = ({ title, htmlFor, isRequired = true, messageError, children }) => (
  <div>
    <Label label={title} htmlFor={htmlFor || title} required={isRequired} />
    {children}
    {messageError && (
      <span className={STBoardForm.errorMessage}>{messageError}</span>
    )}
  </div>
);

const Loading = () => {
  return <></>;
};

const PendingApprovalComponent = ({
  onBackToBoardList,
}: {
  onBackToBoardList: () => void;
}) => {
  useEffect(() => window.scrollTo(0, 0), []);
  const history = useHistory();
  const handleBackToCategoryList = () => {
    history.push("/message-board/categories");
  };
  return (
    <div className={`${STBoardForm.pendingApproval}`}>
      <span className={`${STBoardForm.title}`}>投稿申請完了</span>
      <span className={`${STBoardForm.description}`}>
        {
          "投稿ありがとうございます！\nただいま確認中ですので、公開まで少しだけお待ちください！"
        }
      </span>
      <div className={STBoardForm.pendingApprovalBoardButtonGroup}>
        <button
          onClick={onBackToBoardList}
          className={STBoardForm.navigateButton}
        >
          掲示板カテゴリ一覧に戻る＞
        </button>
        <button
          onClick={handleBackToCategoryList}
          className={STBoardForm.navigateButton}
        >
          掲示板Topページへ戻る＞
        </button>
      </div>
    </div>
  );
};

const FormBoardContent = () => {
  const history = useHistory();
  const query = new URLSearchParams(useLocation().search);
  const query_url_category_id = query.get("board_category_id")
    ? Number(query.get("board_category_id"))
    : 0;
  const params = useParams<{ category_id?: string; board_id?: string }>();
  const { category_id = 0, board_id = 0 } = params ?? {};
  const [data, setData] = useState<any>({});

  const {
    control,
    handleSubmit,
    register,
    setValue,
    getValues,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      board_category_id: Number(query_url_category_id || category_id),
    },
    mode: "all",
  });
  const [snackbarInfor, setSnackbarInfor] = useState<any>({
    severity: "",
    message: "",
  });
  const handleClose = () => {
    setSnackbarInfor({
      severity: "",
      message: "",
    });
  };
  const site = useContext(SiteContext);
  const { isAutoBoardApproval = 0 } = site ?? {};
  const currentUser = useContext(LoginUserContext);
  const { uid } = currentUser?.loginUser ?? {};
  const textSubmitButton =
    isAutoBoardApproval !== FORUM_APPROVAL_SETTING.WITHOUT_APPROVAL
      ? "申請する"
      : "投稿する";
  const { isLoading, boardCategories } = useGetBoardCategories();
  const { isFormLoading, onSubmit, isPendingApproval } =
    useMutationBoardForm(setSnackbarInfor);
  const formFields = useFormConfig(boardCategories);
  const isDisable = !isDirty || !isValid || isFormLoading;

  const handleBackToBoardList = () => {
    const boardCategoryId = getValues("board_category_id");
    history.push(`/message-board/categories/${boardCategoryId}`);
  };

  useEffect(() => {
    if (board_id && !!Object.keys(data ?? {}).length) {
      setValue("board_category_id", data?.board_category_id);
      setValue("title", data?.title);
      setValue("content", data?.content);
      if (Array.isArray(data?.image_urls) && data?.image_urls.length > 0) {
        setValue("pictures", data?.image_urls);
      }
    }
  }, [data, setValue, board_id]);

  useEffect(() => {
    const getBoardDetail = async () => {
      if (!board_id || !category_id) {
        return;
      }
      try {
        await fetchEditBoardDetail(Number(category_id), Number(board_id)).then(
          (res) => {
            if (res.isSuccess) {
              const boardDetail = res?.result?.data;
              if (boardDetail) setData(boardDetail);
            } else if (res.status === 404) {
              history.push("/");
            }
          }
        );
      } catch (error) {
        console.log(error);
      }
    };
    getBoardDetail();
  }, [board_id, category_id]);

  const goBack = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    history.goBack();
  };

  const uploadRef = useRef<any>(null);
  const renderField = ([fieldName, config]: [string, FormField]) => {
    const { type, rules, placeholder, options = [], title } = config;
    const fieldKey = fieldName as keyof FormValues;
    const errorMessage = errors?.[fieldKey]?.message as string;
    switch (type) {
      case "upload_file":
        return (
          <Controller
            key={fieldKey}
            control={control}
            name={fieldKey}
            rules={rules}
            render={({ field, formState: { errors } }) => {
              const { name, onChange } = field;
              let list: any = [];
              const value: any | FileList = field.value;
              if (value instanceof FileList) {
                list = [...list, ...Array.from(value)];
              }
              if (Array.isArray(value) && value.length !== 0) {
                list = [...list, ...value];
              }
              const errorInfor = errors?.[name];
              const errorMessage = errorInfor?.message ?? "";
              return (
                <FieldWrapper
                  title={title}
                  messageError={errorMessage}
                  isRequired={false}
                >
                  <p
                    style={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      color: "#111111",
                      paddingLeft: 12,
                    }}
                  >
                    ※画像を選択（３枚まで）
                  </p>
                  <input
                    accept="image/*"
                    multiple
                    type="file"
                    id={fieldKey}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const selectedFiles: FileList | null = e?.target?.files;
                      if (!selectedFiles?.length) {
                        return;
                      }
                      let selectedFiledToArray = Array.from(selectedFiles);
                      if (Array.isArray(list) && list.length !== 0) {
                        selectedFiledToArray = [
                          ...list,
                          ...selectedFiledToArray,
                        ];
                      }
                      let target: any = [];
                      const filesArray =
                        Array.from(selectedFiledToArray).slice(-3);
                      const fileBuffer = new DataTransfer();
                      filesArray.forEach((file: any) => {
                        if (file instanceof File) {
                          fileBuffer.items.add(file);
                        } else {
                          target.push(file);
                        }
                      });
                      const fieldValue = [
                        ...Array.from(target),
                        ...Array.from(fileBuffer.files),
                      ] as any;
                      if (e?.target) {
                        e.target.value = "";
                      }
                      onChange(fieldValue, {
                        shouldDirty: true,
                      });
                    }}
                    className={STForm.form}
                    style={{ display: "none" }}
                    ref={uploadRef}
                  />
                  <button
                    onClick={(event) => {
                      event.preventDefault();
                      uploadRef.current?.click();
                      return null;
                    }}
                    className={`${STBoardForm.uploadButton} ${STButton.primary}`}
                  >
                    画像を選択
                  </button>
                  <div className={STBoardForm.previewImage}>
                    {Array.isArray(list) &&
                      list.length > 0 &&
                      list?.map((file: any, index: number) => {
                        const src =
                          typeof file === "string"
                            ? file
                            : URL.createObjectURL(file);
                        return (
                          <div
                            key={`selected_picture_${index}`}
                            className={`${STBoardForm.wrapperImage} ${
                              errorMessage ? STBoardForm.errorField : ""
                            }`}
                          >
                            <img
                              src={src}
                              alt={`board_image_upload_${index}`}
                              className={`${STBoardForm.uploadItem} forum-board-image`}
                            />
                            <button
                              onClick={(event) => {
                                event.preventDefault();
                                const newValue = [...value].filter(
                                  (_, i) => i !== index
                                ) as any;
                                if (
                                  newValue.length === 0 &&
                                  uploadRef.current
                                ) {
                                  uploadRef.current.value = "";
                                }
                                setValue(fieldKey, newValue, {
                                  shouldDirty: true,
                                });
                                trigger();
                              }}
                              className={STBoardForm.removeImageButton}
                            >
                              <CloseIcon className={STBoardForm.removeIcon} />
                            </button>
                          </div>
                        );
                      })}
                  </div>
                </FieldWrapper>
              );
            }}
          />
        );

      case "textarea":
        return (
          <FieldWrapper
            key={fieldKey}
            title={title}
            messageError={errorMessage}
          >
            <textarea
              className={`${STForm.form} ${
                errorMessage ? STBoardForm.errorField : ""
              }`}
              placeholder={placeholder}
              {...register(fieldKey, rules)}
            />
          </FieldWrapper>
        );

      case "select":
        return (
          <FieldWrapper
            key={fieldKey}
            title={title}
            messageError={errorMessage}
          >
            <select
              className={`${STSelectBox.selectbox} ${STForm.form} ${
                errorMessage ? STBoardForm.errorField : ""
              }`}
              {...register(fieldKey, rules)}
            >
              {options.map(({ id, value, title }) => (
                <option key={id} value={value}>
                  {title}
                </option>
              ))}
            </select>
          </FieldWrapper>
        );

      default:
        return (
          <FieldWrapper
            key={fieldKey}
            title={title}
            messageError={errorMessage}
          >
            <input
              type={type}
              className={`${STForm.form} ${
                errorMessage ? STBoardForm.errorField : ""
              }`}
              placeholder={placeholder}
              {...register(fieldKey, rules)}
            />
          </FieldWrapper>
        );
    }
  };

  console.log(site.forumGuideUrl);

  if (isLoading) {
    return <Loading />;
  }

  if (!!board_id && !!uid && !!data?.uid && uid !== data?.uid) {
    return (
      <div className={STBoardForm.noEditBoard}>
        記事はまだ承認されていないか、この記事を編集する権限がありません。
      </div>
    );
  }
  return (
    <Fragment>
      {isPendingApproval ? (
        <PendingApprovalComponent onBackToBoardList={handleBackToBoardList} />
      ) : (
        <form
          className={STBoardForm.formWrapper}
          onSubmit={handleSubmit(onSubmit)}
        >
          <p className={STBoardForm.title}>投稿を作成</p>
          {site.forumGuideUrl && (
            <Link
              href={site.forumGuideUrl}
              className={STBoardForm.guideLink}
              target="_blank"
              style={{ textAlign: "right" }}
            >
              {site.forumGuideText}
              <span className={STBoardForm.arrowRight}>
                <span> </span>
                {">"}
              </span>
            </Link>
          )}
          <div className={STBoardForm.formFields}>
            {Object.entries(formFields).map((field) => {
              if (field[0] === "avatar") {
                return (
                  <div>
                    <Label label="表示名" htmlFor="表示名" />
                    <div style={{ paddingTop: "8px" }}>
                      <MemberUpdateNicknameAndAvatar />
                    </div>
                  </div>
                );
              } else {
                return renderField(field as [string, FormField]);
              }
            })}
          </div>
          <div className={STBoardForm.buttonGroup}>
            <button
              className={`${STBoardForm.backButton} ${STButton.secondary}`}
              onClick={goBack}
            >
              戻る
            </button>
            <button
              className={`${STBoardForm.submitButton} ${STButton.primary}`}
              type="submit"
              disabled={isDisable}
              style={{
                opacity: isDisable ? 0.5 : 1,
              }}
            >
              {textSubmitButton}
            </button>
          </div>
        </form>
      )}
      <SnackbarMessage
        {...snackbarInfor}
        open={!!snackbarInfor?.message}
        handleClose={handleClose}
      />
    </Fragment>
  );
};
const FormBoard = () => {
  return (
    <div className={STBoardForm.container}>
      <FormBoardContent />
    </div>
  );
};

export default FormBoard;
